import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import Pagination from '../../components/Pagination/Pagination';
import { useSendWeeklyReports } from '../../hooks/useSendWeeklyReports';
import { useUpdateWeeklyAiReport } from '../../hooks/useUpdateWeeklyAiReport';
import { useWeeklyReports } from '../../hooks/useWeeklyReports';
import { sortReports } from '../../utils/sortReports';
import {
  AIResponseTextarea,
  CheckboxContainer,
  CheckIcon,
  Container,
  DateTimeCell,
  FilterButton,
  FilterContainer,
  Header,
  NoDataMessage,
  ResponseText,
  SaveButton,
  Select,
  SendAllButton,
  StatusBadge,
  StyledCheckbox,
  Table,
  TableHeader,
  TableRow,
  Title,
} from './WeeklyReport.Styles';
import { useBulkUpdateWeeklyAiReports } from '../../hooks/useUpdateWeeklyAiReports';

/**
 * @module WeeklyResponses
 * @description Component for managing weekly user responses and AI-generated reports in the Mental Health Portal application.
 * Allows administrators to view, filter, edit, recommend resources, and send weekly reports.
 *
 * @requires react
 * @requires react-i18next
 * @requires react-oidc-context
 * @requires ../../components/Pagination/Pagination
 * @requires ../../hooks/useSendWeeklyReports
 * @requires ../../hooks/useUpdateWeeklyAiReport
 * @requires ../../hooks/useWeeklyReports
 * @requires ../../utils/sortReports
 * @requires ./WeeklyReport.Styles
 */
const WeeklyResponses = () => {
  /** Translation hook for internationalization */
  const { t } = useTranslation();

  /**
   * @state {Array<Object>} reports - List of weekly reports with their data and UI state
   * @property {string} id - Report ID
   * @property {string} response - User's original response
   * @property {string} date - Formatted date string
   * @property {string} time - Formatted time string
   * @property {string} aiResponse - AI-generated response content
   * @property {Object|null} resource - Recommended resource or null
   * @property {boolean} reviewed - Whether the report has been reviewed
   * @property {boolean} originalEdited - Whether the report was already edited when loaded
   * @property {Date} rawDate - JavaScript Date object for sorting
   */
  const [reports, setReports] = useState([]);

  /**
   * @state {string} filter - Current filter for reports ('all', 'reviewed', or 'notReviewed')
   */
  const [filter, setFilter] = useState('all');

  /**
   * @state {Array<Object>} availableResources - List of resources that can be recommended
   * @property {number} id - Resource ID
   * @property {string} title - Resource title
   */
  const [availableResources, setAvailableResources] = useState([]);

  /**
   * @state {number} page - Current page number for pagination
   */
  const [page, setPage] = useState(1);

  /**
   * @const {number} pageSize - Number of reports to display per page
   */
  const pageSize = 20;

  /**
   * Filters reports based on current filter state
   * @function getFilteredReports
   * @returns {Array} Filtered reports array
   */
  const getFilteredReports = () => {
    if (filter === 'reviewed') return reports.filter((r) => r.reviewed);
    if (filter === 'notReviewed') return reports.filter((r) => !r.reviewed);
    return reports;
  };

  /**
   * Custom hook for fetching weekly reports based on filters and pagination
   * @type {Object}
   * @property {Object} data - Response data containing reports and available resources
   * @property {boolean} loading - Loading state for reports
   * @property {string|null} error - Error message if fetch fails
   */
  const { data, loading, error } = useWeeklyReports({
    all: true,
    page,
    pageSize,
  });

  /** Custom hook for updating weekly AI reports and recommended resources */
  const { updateWeeklyAiReport } = useUpdateWeeklyAiReport();

  /** Custom hook for updating weekly AI reports and recommended resources in BULK */
  const { updateWeeklyAiReports } = useBulkUpdateWeeklyAiReports();

  /** Custom hook for sending reviewed weekly reports */
  const { sendWeeklyReports } = useSendWeeklyReports();

  /** Authentication hook for user management */
  const auth = useAuth();

  /** Total number of pages for pagination */
  const totalPages = Math.ceil((data?.total || 0) / pageSize);

  /** User ID from authentication */
  const userId = auth.user?.profile?.sub;

  /**
   * Effect hook to format and sort reports when data is loaded from API
   * @effect
   * @param {Object} data - The reports data from API
   * @returns {void}
   */
  useEffect(() => {
    if (!data || !data.reports || data.reports.length === 0) return;

    // Transform API report format to component state format
    const newFormattedReports = data.reports.map((report) => ({
      id: report.reportId,
      response: report.answer,
      date: new Date(report.date).toLocaleDateString(),
      time: new Date(report.date).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      aiResponse: report.aiReport,
      resource: report.recommendedResource || null,
      reviewed: report.edited,
      originalEdited: report.edited,
      rawDate: new Date(report.date),
      sentResourceIds: report.sentResourceIds || [],
    }));

    // Sort reports and update state
    setReports(sortReports(newFormattedReports));

    // Update available resources
    setAvailableResources(data.availableResources || []);
  }, [data]);

  /**
   * Updates a report with new fields and moves it to the top of the list
   * @function moveReportToTop
   * @param {string} id - ID of the report to update
   * @param {Object} updatedFields - New fields to add to the report
   * @returns {void}
   */
  const moveReportToTop = (id, updatedFields = {}) => {
    setReports((prev) => {
      const updatedReports = prev.map((r) =>
        r.id === id ? { ...r, ...updatedFields } : r
      );

      return sortReports(updatedReports);
    });
  };

  /**
   * Updates the AI response text and marks it as reviewed
   * @function handleAIResponseChange
   * @param {string} id - ID of the report to update
   * @param {string} newValue - New AI response text
   * @returns {void}
   */
  const handleAIResponseChange = (id, newValue) => {
    setReports((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, aiResponse: newValue, reviewed: true } : r
      )
    );
  };

  /**
   * Updates the recommended resource for a report
   * @function handleResourceChange
   * @param {string} id - ID of the report to update
   * @param {string} selectedId - ID of the selected resource
   * @returns {void}
   */
  const handleResourceChange = (id, selectedId) => {
    const selectedResource = availableResources.find(
      (r) => r.id === parseInt(selectedId)
    );
    moveReportToTop(id, {
      resource: selectedResource || 'Nenhum',
      reviewed: true,
    });
  };

  /**
   * Toggles the reviewed status of a report
   * @function toggleReviewed
   * @param {string} id - ID of the report to toggle
   * @returns {void}
   */
  const toggleReviewed = (id) => {
    const report = reports.find((r) => r.id === id);
    if (report) {
      moveReportToTop(id, { reviewed: !report.reviewed });
    }
  };

  /**
   * Sends all reviewed reports
   * @function sendAllReviewed
   * @async
   * @returns {Promise<void>}
   * @throws {Error} If sending fails
   */
  const sendAllReviewed = async () => {
    const reviewedReports = reports.filter((r) => r.reviewed);
    if (reviewedReports.length === 0) {
      alert(`${t('No revised report to send')}`);
      return;
    }

    try {
      // Send all reviewed reports
      await sendWeeklyReports();

      // Show success message and update UI
      alert(`${reviewedReports.length} ${t('reports sent successfully')}`);
      setReports((prev) =>
        prev.map((r) =>
          r.reviewed ? { ...r, sent: true, reviewed: false } : r
        )
      );
    } catch (err) {
      console.error('Erro ao enviar relatórios:', err);
      alert(`${t('Failed to send reports')}`);
    }
  };

  /**
   * Saves an edited report to the database
   * @function saveReport
   * @async
   * @param {string} id - ID of the report to save
   * @returns {Promise<void>}
   * @throws {Error} If saving fails
   */
  const saveReport = async (id) => {
    const reportToSave = reports.find((r) => r.id === id);

    if (!reportToSave) {
      alert(`${t('Report not found')}`);
      return;
    }

    try {
      // Save report to database with AI response and resource
      await updateWeeklyAiReport({
        id,
        newAiReport: reportToSave.aiResponse,
        userId: userId,
        resourceId: reportToSave.resource?.id,
      });

      // Show success message and update UI
      alert(`${t('Report')} ${id} ${t('saved successfully')}`);
      moveReportToTop(id);
    } catch (error) {
      console.error('Error saving report:', error);
      alert(`${t('Error saving report')}`);
    }
  };

  /** Check if there are any reviewed reports */
  const hasReviewedReports = reports.some((r) => r.reviewed);

  const saveAllReviewed = async () => {
    const reviewedReports = reports.filter((r) => r.reviewed);

    if (reviewedReports.length === 0) {
      alert(`${t('No revised report to save')}`);
      return;
    }

    try {
      await updateWeeklyAiReports(reviewedReports);

      alert(`${reviewedReports.length} ${t('reports saved successfully')}`);

      // Optionally update the UI
      // setReports((prev) =>
      //   prev.map((r) =>
      //     r.reviewed ? { ...r, sent: true, reviewed: false } : r
      //   )
      // );
    } catch (err) {
      console.error('Erro ao salvar relatórios:', err);
      alert(`${t('Failed to save reports')}`);
    }
  };

  const groupedResources = availableResources.reduce((groups, resource) => {
    const category = resource.category_name || t('Other');
    if (!groups[category]) groups[category] = [];
    groups[category].push(resource);
    return groups;
  }, {});

  return (
    <Container>
      <Header>
        <Title>{t('weeklyReport.title')}</Title>
        <div>
          <SendAllButton
            onClick={saveAllReviewed}
            disabled={!hasReviewedReports}
          >
            {t('Save revised reports')}
          </SendAllButton>
          <SendAllButton
            onClick={sendAllReviewed}
            disabled={!hasReviewedReports}
          >
            {t('Submit revised reports')}
          </SendAllButton>
        </div>
      </Header>

      <FilterContainer>
        <FilterButton
          active={filter === 'all'}
          onClick={() => {
            setFilter('all');
            setPage(1);
          }}
        >
          {t('All filter')}
        </FilterButton>
        <FilterButton
          active={filter === 'reviewed'}
          onClick={() => {
            setFilter('reviewed');
            setPage(1);
          }}
        >
          {t('Reviewed filter')}
        </FilterButton>
        <FilterButton
          active={filter === 'notReviewed'}
          onClick={() => {
            setFilter('notReviewed');
            setPage(1);
          }}
        >
          {t('Not Reviewed filter')}
        </FilterButton>
      </FilterContainer>

      {loading ? (
        <NoDataMessage>{t('Loading Reports')}</NoDataMessage>
      ) : error ? (
        <NoDataMessage>{t('Error loading reports')}</NoDataMessage>
      ) : getFilteredReports().length === 0 ? (
        <NoDataMessage>{t('No Reports Found')}.</NoDataMessage>
      ) : (
        <Table>
          <TableHeader>
            <div>{t('User Response')}</div>
            <div>{t('Date')}</div>
            <div>{t('AI Return')}</div>
            <div>{t('Send resource')}</div>
            <div>{t('checklist reviewed')}</div>
          </TableHeader>

          {getFilteredReports().map((report) => (
            <TableRow key={report.id}>
              <ResponseText>{report.response}</ResponseText>

              <DateTimeCell>
                <div>{report.date}</div>
                <div>{report.time}</div>
                {report.originalEdited && (
                  <StatusBadge edited={true}>Editado</StatusBadge>
                )}
              </DateTimeCell>

              <AIResponseTextarea
                value={report.aiResponse}
                onChange={(e) =>
                  handleAIResponseChange(report.id, e.target.value)
                }
                placeholder="Resposta da IA"
              />

              <Select
                value={report.resource?.id || ''}
                onChange={(e) =>
                  handleResourceChange(report.id, e.target.value)
                }
              >
                <option value="">{t('None')}</option>
                {Object.entries(groupedResources).map(
                  ([category, resources]) => (
                    <optgroup key={category} label={category}>
                      {resources.map((resource) => {
                        const alreadySent = report.sentResourceIds?.includes(
                          Number(resource.id)
                        );

                        return (
                          <option key={resource.id} value={resource.id}>
                            {alreadySent
                              ? `✅ ${resource.title}`
                              : resource.title}
                          </option>
                        );
                      })}
                    </optgroup>
                  )
                )}
              </Select>

              <CheckboxContainer>
                <StyledCheckbox
                  checked={report.reviewed}
                  onClick={() => toggleReviewed(report.id)}
                >
                  {report.reviewed && <CheckIcon>✓</CheckIcon>}
                </StyledCheckbox>
                <SaveButton onClick={() => saveReport(report.id)}>
                  {t('Save Button')}
                </SaveButton>
              </CheckboxContainer>
            </TableRow>
          ))}
        </Table>
      )}
      {getFilteredReports().length > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </Container>
  );
};

export default WeeklyResponses;
